/**
 * SectionLevelGaps — paid-audit variant of the free-score GapBlock.
 *
 * Renders quoted verbatim callouts for the user's headline + About text
 * (analysed by the same Gemini 2.5 Flash pipeline as /api/free-score/from-pdf).
 * The paid audit's LLM writes a full rewrite too — this card is the
 * "why this needs fixing" diagnostic sitting *above* the rewrite.
 *
 * Renders nothing if the analysis doesn't yet have either gap (older
 * audits pre-dating this feature will simply not show the card).
 */
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Quote } from 'lucide-react';

function Gap({ label, gap, testid }) {
  if (!gap || (!gap.quote && !gap.issue && !gap.fix)) return null;
  return (
    <div
      className="rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-4"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-500/15 text-amber-500">
          <Quote size={12} strokeWidth={2.4} />
        </span>
        <p className="text-[10.5px] font-mono-edge tracking-[0.22em] uppercase text-amber-500">
          {label}
        </p>
      </div>
      {gap.quote && (
        <blockquote className="border-l-2 border-amber-500/70 pl-3 text-[13px] italic text-foreground/90 leading-snug mb-2">
          &ldquo;{gap.quote}&rdquo;
        </blockquote>
      )}
      {gap.issue && (
        <p className="text-[13px] text-muted-foreground leading-relaxed">
          <span className="font-semibold text-foreground">Why it&rsquo;s weak: </span>
          {gap.issue}
        </p>
      )}
      {gap.fix && (
        <p className="mt-1.5 text-[13px] leading-relaxed">
          <span className="font-semibold text-primary">Fix: </span>
          <span className="text-foreground/90">{gap.fix}</span>
        </p>
      )}
    </div>
  );
}

export function SectionLevelGaps({ headlineGap, aboutGap }) {
  if (!headlineGap && !aboutGap) return null;
  return (
    <Card className="border-border" data-testid="analysis-section-level-gaps">
      <CardContent className="p-6">
        <div className="mb-3">
          <h3 className="text-lg font-semibold">Section-level gaps</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Real snippets from your profile that Blayzrr flagged for this objective.
            Each rewrite in the tabs below directly addresses one of these.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Gap label="Headline gap" gap={headlineGap} testid="analysis-headline-gap" />
          <Gap label="About-section gap" gap={aboutGap} testid="analysis-about-gap" />
        </div>
      </CardContent>
    </Card>
  );
}

export default SectionLevelGaps;
